<template>
  <v-container fluid pa-1 class="mt-10 pb-10">
    <div class="row d-flex justify-center">
      <div class="col-12 col-md-6">
        <div class="d-flex pt-5 mb-3">
          <h1 class="mx-auto">
            Deal Summary
            <span v-if="isApproved" class="stamp float-end">Approved</span>
            <span v-if="isRejectedDeal" class="stamp float-end rejected">Rejected</span>
          </h1>
        </div>
        <v-divider></v-divider>
        <!-- The client is shown always -->
        <DealClientSummary
          :businessEntityName="deal.client.businessEntityName"
          :clientName="deal.client.name"
          :invoiceFrequencyId="deal.client.invoiceFrequencyId"
        />
        <!-- These three sections are shown conditionally -->
        <div v-if="isContractor">
          <DealContractSummary
            :firstName="deal.candidates[0].firstName"
            :lastName="deal.candidates[0].lastName"
            :emailAddress="deal.candidates[0].emailAddress"
            :jobTitle="deal.candidates[0].dealAssignment.jobTitle"
            :employeeTypeId="deal.candidates[0].dealAssignment.employeeTypeId"
            :corporateName="deal.candidates[0].dealAssignment.corporateName"
            :ein="deal.candidates[0].dealAssignment.ein"
            :contractTypeId="deal.candidates[0].dealAssignment.contractTypeId"
            :lengthOfContract="deal.candidates[0].dealAssignment.lengthOfContract"
            :conversionFee="deal.candidates[0].dealAssignment.conversionFee"
            :payFrequencyId="deal.candidates[0].dealAssignment.payFrequencyId"
            :payRateTypeId="deal.candidates[0].dealAssignment.payRateTypeId"
            :payRate="deal.candidates[0].dealAssignment.payRate"
            :billRateTypeId="deal.candidates[0].dealAssignment.billRateTypeId"
            :billRate="deal.candidates[0].dealAssignment.billRate"
            :overtimeEligible="deal.candidates[0].dealAssignment.overtimeEligible"
            :startAt="deal.candidates[0].dealAssignment.startAt"
            :endAt="deal.candidates[0].dealAssignment.endAt"
            :commissions="deal.candidates[0].dealAssignment.commissions"
            :expenses="deal.candidates[0].dealAssignment.expenses"
          />
        </div>
        <div v-if="isPermPlacement">
          <DealPermanentPlacementSummary
            :firstName="deal.candidates[0].firstName"
            :lastName="deal.candidates[0].lastName"
            :emailAddress="deal.candidates[0].emailAddress"
            :jobTitle="deal.candidates[0].dealAssignment.jobTitle"
            :permPlacementDate="deal.candidates[0].dealAssignment.permPlacementDate"
            :permPlacementInvoiceDate="deal.candidates[0].dealAssignment.permPlacementInvoiceDate"
            :permPlacementInvoiceDueDate="deal.candidates[0].dealAssignment.permPlacementInvoiceDueDate"
            :permPlacementSalary="deal.candidates[0].dealAssignment.permPlacementSalary"
            :permPlacementFee="deal.candidates[0].dealAssignment.permPlacementFee"
            :permPlacementFeeTypeId="deal.candidates[0].dealAssignment.permPlacementFeeTypeId"
            :commissions="deal.candidates[0].dealAssignment.commissions"
            :expenses="deal.candidates[0].dealAssignment.expenses"
          />
        </div>
        <div v-if="isProject">
          <DealProjectSummary
            :candidates="deal.candidates"
            :projectName="deal.project.projectName"
          />
        </div>
        <SummaryTitle v-if="deal.notes" title="Rejection Comments"></SummaryTitle>
        <div class="row" v-if="deal.notes">
          <div class="col-12 justify-start px-10">
            <p style="white-space: pre-line" class="text-justify" v-html="deal.notes"></p>
          </div>
        </div>
        <v-row justify="center" class="mt-3">
          <v-col v-if="showApproveDealButton" cols="12" sm="6">
            <v-btn
              block
              depressed
              color="success"
              @click="approveDeal"
            >
              Approve Deal
            </v-btn>
          </v-col>
          <v-col v-if="showRejectDealButton" cols="12" sm="6">
            <v-btn
              block
              depressed
              color="warning"
              @click="() => showRejectedModal = true"
            >
              Reject Deal
            </v-btn>
          </v-col>
          <v-col v-if="showSubmitDealButton" cols="12" sm="6">
            <v-btn
              block
              depressed
              color="primary"
              @click="submitDeal"
            >
              Submit
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </div>
    <DealRejectedModal
      :showModal="showRejectedModal"
      @reject="rejectDeal"
      @closeDialog="showRejectedModal = false"
    >
    </DealRejectedModal>
  </v-container>
</template>

<script>
import { mapFields } from '@/vueHelpers/vuex'
import { dealStatuses } from '@/lib/enum/dealStatuses'
import { userIsAdmin } from '@/lib/authentication'
import { CONTRACTOR, PERMANENT_PLACEMENT, PROJECT } from '@/lib/constants/dealAssignmentTypes'
import { SummaryTitle } from '@/components/shared/display'
import DealClientSummary from './DealClientSummary.vue'
import DealContractSummary from './DealContractSummary.vue'
import DealPermanentPlacementSummary from './DealPermanentPlacementSummary.vue'
import DealProjectSummary from './DealProjectSummary.vue'
import DealRejectedModal from './DealRejectedModal.vue'

export default {
  components: {
    SummaryTitle,
    DealClientSummary,
    DealContractSummary,
    DealPermanentPlacementSummary,
    DealProjectSummary,
    DealRejectedModal
  },
  props: [
    'readonly'
  ],
  data: () => ({
    showRejectedModal: false,
    isFormValid: false,
    dealStatuses,
    responsibleId: null,
    dueDate: null,
    taskNotes: null,
    previousStatusId: null
  }),
  computed: {
    ...mapFields('dealV2', ['deal']),
    isRejectedDeal () {
      return this.deal.statusId === dealStatuses.Rejected
    },
    isApproved () {
      return this.deal.statusId === dealStatuses.Approved
    },
    isContractor () {
      return this.deal.dealAssignmentType === CONTRACTOR
    },
    isPermPlacement () {
      return this.deal.dealAssignmentType === PERMANENT_PLACEMENT
    },
    isProject () {
      return this.deal.dealAssignmentType === PROJECT
    },
    showSubmitDealButton () {
      return (
        // Only shown to non-admin users
        !userIsAdmin() &&
        // If the deal doesn't have status
        (!this.deal.statusId ||
          // If the deal is rejected
          this.deal.statusId === dealStatuses.Rejected ||
          // If the deal hasn't been submitted already
          this.deal.statusId === dealStatuses.InProgress
        ) &&
        !this.readonly
      )
    },
    showApproveDealButton () {
      return (
        // Only shown to admins
        userIsAdmin() &&
        // When the deal is not approved yet
        !this.isApproved &&
        // And the deal is not Readonly
        !this.readonly
      )
    },
    showRejectDealButton () {
      return (
        // Only shown to admins
        userIsAdmin() &&
        // When the status is submitted (New)
        this.deal.statusId === dealStatuses.New &&
        // Deal is not readonly
        !this.readonly
      )
    }
  },
  methods: {
    submitDeal () {
      this.$emit('submitDeal')
    },
    approveDeal () {
      this.$emit('approveDeal')
    },
    rejectDeal (notes) {
      this.$emit('rejectDeal', notes)
    }
  }
}
</script>
<style>
.stamp {
  text-align: center;
  opacity: 0.5;
  transform: rotate(27deg);
  position: absolute;
  display: inline-block;
  color: green;
  padding: 15px;
  width: 15rem;
  background-color: white;
  box-shadow: inset 0px 0px 0px 10px green
}

.rejected {
  color: red !important;
  box-shadow: inset 0px 0px 0px 10px red !important;
}

@media (max-width: 576px) {
  .stamp {
    opacity: 0.3;
    padding: 12px !important;
    top: 5em !important;
    right: 1em;
    transform: rotate(0deg) !important;
  }
}

@media (min-width: 577px) {
  .stamp {
    top: 2em;
    right: 1em;
  }
}

@media (min-width: 768px) {
  .stamp {
    top: 3em;
    right: 1em;
  }
}

@media (min-width: 992px) {
  .stamp {
    top: 3em;
    right: 4em;
  }
}

@media (min-width: 1200px) {
  .stamp {
    top: 3em;
    right: 5em;
  }
}

@media (min-width: 1400px) {
  .stamp {
    top: 3em;
    right: 6em;
  }
}

@media (min-width: 1600px) {
  .stamp {
    top: 3em;
    right: 8em;
  }
}

@media (min-width: 1800px) {
  .stamp {
    top: 3em;
    right: 9em;
  }
}

@media (min-width: 2000px) {
  .stamp {
    top: 3em;
    right: 10em;
  }
}

@media (min-width: 2200px) {
  .stamp {
    top: 3em;
    right: 11em;
  }
}

@media (min-width: 2400px) {
  .stamp {
    top: 3em;
    right: 12em;
  }
}

.stamp:after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-image: url('../../../../assets/stampTexture.jpg');
  mix-blend-mode: lighten;
}
</style>
